import { cn } from '../lib/cn';

/**
 * The Backyard brand mark — a leaf that curls into a "b" with a sprouting leaf on top,
 * echoing the garden-café identity. Uses `currentColor` so it inherits the surrounding
 * text colour (white on the dark hero, accent gold on light surfaces).
 */
export function LeafBMark({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Ascender / stem of the "b" */}
      <path
        d="M20 6 C 17.6 6 16.2 7.8 16.2 11 L 16.2 50 C 16.2 52.4 17.4 53.8 19.6 54.2"
        stroke="currentColor"
        strokeWidth="4.6"
        strokeLinecap="round"
      />
      {/* Leaf bowl of the "b" (pointed leaf) */}
      <path
        d="M16.2 30 C 30 24 46 27.5 49 39.5 C 51 50 41.5 57 30 55.2 C 22 54 16.2 48.5 16.2 40.5 Z"
        fill="currentColor"
      />
      {/* Midrib vein on the bowl */}
      <path
        d="M20 35.5 L 45 51.5"
        stroke="var(--leaf-vein, rgba(0,0,0,0.20))"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Sprouting leaf at the top */}
      <path
        d="M16.2 16 C 15.4 6.5 21.5 1.5 32 2.5 C 31.2 12 24.6 16.8 16.2 16 Z"
        fill="currentColor"
      />
      <path
        d="M18 9 L 28 5.5"
        stroke="var(--leaf-vein, rgba(0,0,0,0.20))"
        strokeWidth="1.6"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * Full lock-up: the leaf mark in an optional accent badge + the "The Backyard" wordmark
 * (with the "JAIPUR" tag). `brand` overrides the wordmark from the admin config.
 */
export function TheBackyardLogo({
  size = 40,
  badge = false,
  brand = 'The Backyard',
  tag = 'JAIPUR',
  src,
  className,
}: {
  size?: number;
  badge?: boolean;
  brand?: string;
  tag?: string;
  /** Uploaded brand-logo URL (Settings → logo). When set, it replaces the built-in leaf mark. */
  src?: string;
  className?: string;
}) {
  // An admin-uploaded logo (from the tenant config) takes over the mark; falls back to the leaf.
  const mark = src ? (
    <img
      src={src}
      alt={brand}
      style={{ width: size, height: size, objectFit: 'contain' }}
      className={badge ? 'rounded-xl' : ''}
    />
  ) : (
    <LeafBMark size={badge ? Math.round(size * 0.62) : size} />
  );
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      {badge && !src ? (
        <span
          className="flex items-center justify-center rounded-xl bg-[image:var(--gradient-primary)] text-primary-foreground"
          style={{ width: size, height: size }}
        >
          {mark}
        </span>
      ) : (
        mark
      )}
      <span className="flex flex-col leading-none">
        <span className="font-display text-[1.15em] font-extrabold tracking-tight">{brand}</span>
        {tag ? (
          <span className="text-[0.55em] font-semibold uppercase tracking-[0.32em] opacity-70">{tag}</span>
        ) : null}
      </span>
    </div>
  );
}
